import type { ActivityEvidence } from '../../../types';
import { ChangeCard } from './ChangeCard';
import { CountsStrip } from './CountsStrip';
import { LinesBlock } from './LinesBlock';
import { ReasonTag } from './ReasonTag';
import { StatusTransition } from './StatusTransition';

/**
 * One warning per unknown shape per page load.
 *
 * A feed page is 25 rows and a record can hold hundreds; a server that starts emitting a sixth shape would
 * otherwise flood the console once per row per scroll, which is how a useful signal becomes noise nobody
 * reads. Module-level, so it survives re-renders and remounts of the panel.
 */
const warnedShapes = new Set<string>();

function warnUnknownShapeOnce(shape: unknown): void {
  const key = String(shape);
  if (warnedShapes.has(key)) return;
  warnedShapes.add(key);
  console.warn(
    `[activity] Unknown evidence shape "${key}" — block skipped. The server added a shape this client does not render yet.`
  );
}

interface EvidenceBlockProps {
  evidence: ActivityEvidence;
  /** The row's severity tone, forwarded to `note` blocks — see `ReasonTag`. */
  tone?: string | null;
  className?: string;
}

/**
 * The shape switch — the entire contract between the feed and the five renderers (E9 §C1–C3).
 *
 * 🔑 It switches on `shape` and inspects **nothing else**. It never learns an event type, a projection axis
 * or a payload key: the server resolves all of that and hands over finished strings, which is what lets a
 * producer change its payload keys without this file moving.
 *
 * ⚠️ **It must never throw.** An unknown shape renders nothing and logs once — a forward-compatible server
 * deploy must degrade to a slightly thinner row, never to a blank detail page. Note that a `try/catch` here
 * could not provide that guarantee anyway (children render after this function returns), so the defence
 * lives as guards inside each block instead.
 */
export function EvidenceBlock({ evidence, tone, className }: EvidenceBlockProps) {
  if (!evidence) return null;

  switch (evidence.shape) {
    case 'lines':
      return (
        <LinesBlock
          columns={evidence.columns}
          rows={evidence.rows}
          direction={evidence.direction}
          className={className}
        />
      );

    case 'counts':
      return <CountsStrip counts={evidence.counts} className={className} />;

    case 'change':
      return <ChangeCard changes={evidence.changes} className={className} />;

    case 'transition':
      return (
        <StatusTransition
          from={evidence.from}
          to={evidence.to}
          tone={evidence.tone}
          className={className}
        />
      );

    case 'note':
      return <ReasonTag text={evidence.text} tone={tone} className={className} />;

    default:
      warnUnknownShapeOnce(evidence.shape);
      return null;
  }
}
